import { __assign, __extends } from 'tslib';
import { ScriptLoader, liveAnnounce, analyticsTracker } from 'uitk-react-utils';
import { useLocalization } from '@shared-ui/localization-context';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import { forwardRef, createElement, createRef, Fragment, Component } from 'react';
import { UitkFauxInput, UitkTextInput } from 'uitk-react-input';
import 'uitk-react-dialog';
import { T as TypeaheadToolbarVersion, U as UitkTypeaheadInput } from './uitk-typeahead-toolbar-2e6e13c8.js';
export { T as TypeaheadToolbarVersion, a as UitkTypeaheadAttribution, U as UitkTypeaheadInput, c as UitkTypeaheadResultItem, d as UitkTypeaheadResultTitle, b as UitkTypeaheadResults, e as UitkTypeaheadToolbar } from './uitk-typeahead-toolbar-2e6e13c8.js';
import 'uitk-react-empty-state';
import 'uitk-react-icons';
import 'uitk-react-text';
import 'uitk-react-skeleton';
import 'uitk-react-button';
import 'uitk-react-toolbar';
import { UitkTypeaheadDialog } from './uitk-typeahead-dialog.esm.js';
export { UitkTypeaheadDialog } from './uitk-typeahead-dialog.esm.js';
import 'uitk-react-menu';
import { UitkTypeaheadMenu } from './uitk-typeahead-menu.esm.js';
export { TypeaheadMenu, UitkTypeaheadMenu } from './uitk-typeahead-menu.esm.js';

var PlacesServiceStatus;
(function (PlacesServiceStatus) {
    PlacesServiceStatus["INVALID_REQUEST"] = "INVALID_REQUEST";
    PlacesServiceStatus["OK"] = "OK";
    PlacesServiceStatus["OVER_QUERY_LIMIT"] = "OVER_QUERY_LIMIT";
    PlacesServiceStatus["REQUEST_DENIED"] = "REQUEST_DENIED";
    PlacesServiceStatus["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
    PlacesServiceStatus["ZERO_RESULTS"] = "ZERO_RESULTS";
})(PlacesServiceStatus || (PlacesServiceStatus = {}));
var GoogleAdapter = /** @class */ (function () {
    function GoogleAdapter(locale, googleApiKey) {
        var _this = this;
        var apiKey = googleApiKey;
        var url = "//maps.googleapis.com/maps/api/js?client=gme-expedia&channel=ess&libraries=places&language=" + locale + "&key=" + apiKey;
        ScriptLoader.getScript(url)
            .then(function () {
            _this.google = window.google;
            _this.initGooglePlacesService();
        })
            .catch(function (err) {
            console.error(err);
        });
    }
    GoogleAdapter.getInstance = function (locale, googleApiKey) {
        var thisLocale = locale || 'en_US';
        var thisGoogleApiKey = googleApiKey || 'AIzaSyABOFQNLMiTVN0AG3eWNmspVfVC8gNda68';
        if (!(thisLocale in this.instances)) {
            this.instances[thisLocale] = new GoogleAdapter(thisLocale, thisGoogleApiKey);
        }
        return this.instances[thisLocale];
    };
    GoogleAdapter.prototype.initGooglePlacesService = function () {
        if (this.google && this.google.maps && this.google.maps.places) {
            var AutocompleteService = this.google.maps.places.AutocompleteService;
            this.googleAutoCompleteService = new AutocompleteService();
        }
    };
    GoogleAdapter.prototype.fetchResults = function (essResponse) {
        var _this = this;
        var request = {
            input: essResponse.q,
            type: essResponse.rc === 'ADDRESS_AUTOCOMPLETE' ? ['geocode'] : undefined
        };
        var toEssResponse = function (results) {
            return {
                q: essResponse.q,
                rc: essResponse.rc,
                rid: essResponse.rid,
                sr: results
            };
        };
        return new Promise(function (resolve) {
            if (_this.googleAutoCompleteService) {
                _this.googleAutoCompleteService.getPlacePredictions(request, function (predictions, status) {
                    if (status === PlacesServiceStatus.OK) {
                        var response = toEssResponse(_this.handleGoogleResponse(predictions));
                        resolve(response);
                    }
                    else {
                        resolve(essResponse);
                    }
                });
            }
            else {
                resolve(essResponse);
            }
        });
    };
    GoogleAdapter.prototype.handleGoogleResponse = function (predictions) {
        var results = [];
        predictions.forEach(function (prediction, index) {
            var displayName = prediction.description;
            var primaryDisplayName = '';
            var secondaryDisplayName = '';
            var parts = (displayName || '').split(',');
            if (parts.length < 2) {
                primaryDisplayName = parts[0].trim();
            }
            else {
                primaryDisplayName = parts[0];
                secondaryDisplayName = parts
                    .slice(1)
                    .map(function (word) { return word.trim(); })
                    .join(', ')
                    .trim();
            }
            var result = {
                category: 'GOOGLEMAPS',
                coordinates: undefined,
                index: index,
                regionNames: {
                    displayName: displayName,
                    fullName: prediction.description,
                    lastSearchName: prediction.description,
                    primaryDisplayName: primaryDisplayName,
                    secondaryDisplayName: secondaryDisplayName
                },
                sourceId: prediction.place_id,
                sourceName: 'GPA',
                type: 'ADDRESS'
            };
            results.push(result);
        });
        return results;
    };
    GoogleAdapter.instances = {};
    return GoogleAdapter;
}());

var TypeaheadLocationType;
(function (TypeaheadLocationType) {
    TypeaheadLocationType["HOTEL"] = "HOTEL";
    TypeaheadLocationType["AIRPORT"] = "AIRPORT";
    TypeaheadLocationType["MULTICITY"] = "MULTICITY";
    TypeaheadLocationType["POI"] = "POI";
    TypeaheadLocationType["METROCODE"] = "METROCODE";
    TypeaheadLocationType["METROSTATION"] = "METROSTATION";
    TypeaheadLocationType["TRAINSTATION"] = "TRAINSTATION";
    TypeaheadLocationType["ADDRESS"] = "ADDRESS";
    TypeaheadLocationType["NEIGHBORHOOD"] = "NEIGHBORHOOD";
})(TypeaheadLocationType || (TypeaheadLocationType = {}));
var EssFeatures;
(function (EssFeatures) {
    EssFeatures["GOOGLE"] = "google";
    EssFeatures["CONSISTENT_DISPLAY"] = "consistent_display";
})(EssFeatures || (EssFeatures = {}));
var ESSAdapter = /** @class */ (function () {
    function ESSAdapter(config) {
        this.config = config;
        this.config.getDevice = this.getDevice;
        if (config.essLogger) {
            this.initLoggers(config.essLogger);
        }
    }
    ESSAdapter.prototype.setTypeaheadCategories = function () {
        var formatText = this.l10n.formatText;
        this.ESSCategories = {
            ADDRESS: formatText('typeahead.ess.address'),
            AIRPORT: formatText('typeahead.ess.airport'),
            ATTRACTION: formatText('typeahead.ess.attraction'),
            CITY: formatText('typeahead.ess.city'),
            HOTEL: formatText('typeahead.ess.hotel'),
            METROCODE: formatText('typeahead.ess.city'),
            METROSTATION: formatText('typeahead.ess.metroStation'),
            MULTICITY: formatText('typeahead.ess.city'),
            MULTIREGION: formatText('typeahead.ess.city'),
            NEIGHBORHOOD: formatText('typeahead.ess.city'),
            POI: formatText('typeahead.ess.attraction'),
            TRAINSTATION: formatText('typeahead.ess.trainStation')
        };
    };
    ESSAdapter.prototype.getTypeaheadCategories = function () {
        return this.ESSCategories;
    };
    ESSAdapter.prototype.addToESSCategories = function (newCategory) {
        this.ESSCategories = __assign(__assign({}, this.ESSCategories), newCategory);
    };
    ESSAdapter.prototype.setL10n = function (l10n) {
        this.l10n = l10n;
    };
    ESSAdapter.prototype.initLoggers = function (logger) {
        logger.initLogger(this.config);
    };
    ESSAdapter.prototype.constructURL = function (query) {
        var domain = this.config.domain ? this.config.domain : '';
        domain = !domain.endsWith('/') ? domain + '/' : domain;
        domain = (this.config.enableEgta || !query) ? domain + 'egta' : domain;
        if (domain.endsWith('/')) {
            domain = domain.slice(0, -1);
        }
        var baseURL = domain + "/api/v4/typeahead";
        if (typeof this.config.browser === 'undefined' ||
            this.config.browser === null) {
            this.config.browser = this.getBrowser(window.navigator.userAgent);
        }
        if (typeof this.config.device === 'undefined' ||
            this.config.device === null) {
            this.config.device = this.getDevice(window.navigator.userAgent);
        }
        var config = __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ browser: this.config.browser, client: this.config.client, dest: this.config.dest, device: this.config.device, expuserid: this.config.expuserid ? this.config.expuserid : -1, features: this.config.features, format: 'json', guid: this.config.guid, lob: this.config.lob, locale: this.config.locale, maxresults: this.config.maxResults, personalize: this.config.personalize ? this.config.personalize : false }, (this.config.regionId ? { rid: this.config.regionId } : {})), { regiontype: this.config.regionType, siteid: this.config.siteId }), (this.config.packageType ? { pt: this.config.packageType } : {})), (this.config.lastSearchRegionId ? { lsrid: this.config.lastSearchRegionId } : {})), (this.config.lastSearchTimestamp ? { lstime: this.config.lastSearchTimestamp } : {})), (this.config.lastSearchLatLong ? { lslatlong: this.config.lastSearchLatLong } : {})), (this.config.lastSearchElapseTime ? { lsetime: this.config.lastSearchElapseTime } : {})), (this.config.googleApiKey ? { googleApiKey: this.config.googleApiKey } : {}));
        var URLParams = function (params) {
            return Object.entries(params)
                .map(function (_a) {
                var key = _a[0], val = _a[1];
                return key + "=" + encodeURIComponent(val);
            })
                .join('&');
        };
        var url = query !== ''
            ? baseURL + "/" + query + "?" + URLParams(config)
            : baseURL + "/?" + URLParams(config);
        return url;
    };
    ESSAdapter.prototype.fetchResponse = function (term) {
        var _this = this;
        var personalize = this.config && this.config.personalize;
        if ((term === '' && personalize) || term !== '') {
            if (this.config.essLogger) {
                this.config.essLogger.logTypeaheadRequest(term);
            }
            return fetch(this.constructURL(term)).then(function (response) {
                if (_this.config.essLogger) {
                    _this.config.essLogger.logResponse(response);
                }
                return response.json();
            });
        }
        else {
            return Promise.resolve([]);
        }
    };
    ESSAdapter.prototype.fetchResults = function (term) {
        var _this = this;
        if (this.config.essLogger) {
            // this is because the control config is being overriden by variant and we don't want that
            this.config.essLogger.initLogger(this.config);
        }
        return this.fetchResponse(term)
            .then(function (json) { return _this.handleEssResponse(json); })
            .then(function (json) { return _this.logResponse(json); })
            .then(function (json) { return _this.adaptResponseToTypeaheadResults(json, term); });
    };
    ESSAdapter.prototype.fetchRecentSearchResults = function () {
        return this.fetchResults('');
    };
    ESSAdapter.prototype.fetchResultsCategorized = function (term) {
        var _this = this;
        if (this.config.essLogger) {
            // this is because the control config is being overriden by variant and we don't want that
            this.config.essLogger.initLogger(this.config);
        }
        return this.fetchResponse(term)
            .then(function (json) { return _this.handleEssResponse(json); })
            .then(function (json) { return _this.logResponse(json); })
            .then(function (json) { return _this.adaptResponseToTypeaheadResults(json, term); })
            .then(function (json) { return _this.categorizeResponse(json); });
    };
    ESSAdapter.prototype.fetchRecentSearchResultsCategorized = function () {
        return this.fetchResultsCategorized('');
    };
    ESSAdapter.prototype.categorizeResponse = function (typeaheadResults) {
        var _this = this;
        var resultsByCategory = new Map();
        var finalResults = [];
        var trueIndex = 0;
        if (typeaheadResults && typeaheadResults.length > 0) {
            // Creating categories and inserting to a map
            typeaheadResults.forEach(function (element) {
                var _a;
                var categoryName = ((_a = element === null || element === void 0 ? void 0 : element.data) === null || _a === void 0 ? void 0 : _a.type) ? _this.ESSCategories[element.data.type] : '';
                if (!resultsByCategory[categoryName]) {
                    resultsByCategory[categoryName] = [];
                }
                resultsByCategory[categoryName].push(element);
            });
            for (var _i = 0, _a = Object.entries(resultsByCategory); _i < _a.length; _i++) {
                var _b = _a[_i], heading = _b[0], results = _b[1];
                // Pushing to an array of CategorizedResults
                results.forEach(function (typeaheadResult) {
                    typeaheadResult.data.index = trueIndex;
                    trueIndex++;
                });
                var resultByCategory = {
                    heading: heading,
                    typeaheadResults: results,
                };
                finalResults.push(resultByCategory);
            }
        }
        return finalResults;
    };
    ESSAdapter.prototype.logSuggestionShown = function (term, results) {
        if (this.config.essLogger) {
            this.config.essLogger.logSuggestionShown(term);
        }
    };
    ESSAdapter.prototype.logItemSelection = function (suggestion) {
        if (this.config.essLogger) {
            this.config.essLogger.logSuggestionSelected(suggestion);
        }
    };
    ESSAdapter.prototype.typeaheadDialogOpened = function (prefill) {
        if (this.config.essLogger) {
            var prefillText = prefill && prefill !== '' ? prefill : undefined;
            this.config.essLogger.logTypeaheadOpen(prefillText);
        }
    };
    ESSAdapter.prototype.logTypeaheadDismiss = function () {
        if (this.config.essLogger) {
            this.config.essLogger.logTypeaheadBlurEvent();
        }
    };
    ESSAdapter.prototype.handleEssResponse = function (json) {
        json.sr = json.sr || [];
        if (!this.googleAdapter &&
            this.config.features.split('|').indexOf(EssFeatures.GOOGLE) >= 0) {
            this.googleAdapter = GoogleAdapter.getInstance(this.config.locale, this.config.googleApiKey);
        }
        if (this.googleAdapter &&
            (json.rc === 'GOOGLE_AUTOCOMPLETE' || json.rc === 'ADDRESS_AUTOCOMPLETE')) {
            return this.googleAdapter.fetchResults(json);
        }
        return Promise.resolve(json);
    };
    ESSAdapter.prototype.logResponse = function (results) {
        if (this.config.essLogger) {
            this.config.essLogger.logTypeaheadResponse(results);
        }
        return Promise.resolve(results);
    };
    ESSAdapter.prototype.adaptResponseToTypeaheadResults = function (results, term) {
        var _this = this;
        var adapt = function (json) {
            return json.sr && json.sr.length ? json.sr.reduce(generateNewResults, []) : [];
        };
        var getIcon = function (type, subType, isChild, isNearby, category) {
            if (isChild && !isNearby) {
                return 'subdirectory_arrow_right';
            }
            if (category === 'USERHISTORY') {
                return 'history';
            }
            if (_this.config.lob === 'FLIGHTS') {
                return 'lob_flights';
            }
            switch (type) {
                case 'HOTEL':
                    return 'lob_hotels';
                case 'AIRPORT':
                    return 'lob_flights';
                case 'ADDRESS':
                    if (subType === 'RENTALLOCATION') {
                        return 'lob_cars';
                    }
                    else {
                        return 'place';
                    }
                case 'METROSTATION':
                case 'TRAINSTATION':
                    return 'train';
                default:
                    return 'place';
            }
        };
        var getDates = function (category, data) {
            var dateString;
            if (category === 'USERHISTORY' &&
                typeof _this.l10n !== 'undefined' &&
                data &&
                data.checkindate &&
                data.checkoutdate &&
                !term) {
                var formatDateString = _this.l10n.formatDateString;
                var checkinDate = Number(data.checkindate);
                var checkoutDate = Number(data.checkoutdate);
                dateString = formatDateString(new Date(checkinDate), {
                    skeleton: 'MMMd'
                }) + " - " + formatDateString(new Date(checkoutDate), {
                    skeleton: 'MMMd'
                });
            }
            return dateString;
        };
        var parseDisplayText = function (result) {
            var oneLineDisplayText = result.regionNames.displayName;
            var dates = getDates(result.category, result);
            if (_this.config.features.indexOf(EssFeatures.CONSISTENT_DISPLAY) < 0) {
                return { primaryText: oneLineDisplayText, subtext: dates };
            }
            else {
                var firstLine = result.regionNames.primaryDisplayName;
                if (dates) {
                    return {
                        primaryText: "<strong>" + firstLine + "</strong>",
                        subtext: dates
                    };
                }
                var secondLine = result.regionNames.secondaryDisplayName;
                return {
                    primaryText: "<strong>" + firstLine + "</strong>",
                    subtext: secondLine
                };
            }
        };
        var generateNewResults = function (newResults, result, index) {
            var isNearby = (result.hierarchyInfo &&
                result.hierarchyInfo.relation &&
                result.hierarchyInfo.relation.indexOf('nearby') > -1);
            var isChild = (result.hierarchyInfo &&
                result.hierarchyInfo.relation &&
                result.hierarchyInfo.relation.indexOf('child') > -1) ||
                isNearby;
            var _a = parseDisplayText(result), primaryText = _a.primaryText, subtext = _a.subtext;
            var newResult = __assign(__assign({ data: __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({}, (result.category ? { category: result.category } : {})), (!term && result.checkindate
                    ? { checkindate: Number(result.checkindate) }
                    : {})), (!term && result.checkoutdate
                    ? { checkoutdate: Number(result.checkoutdate) }
                    : {})), (result.userHistoryType
                    ? { userHistoryType: result.userHistoryType }
                    : {})), (result.coordinates
                    ? {
                        location: {
                            lat: result.coordinates.lat,
                            long: result.coordinates.long
                        }
                    }
                    : {})), (result.regionId
                    ? { regionId: result.regionId }
                    : result.gaiaId
                        ? { regionId: result.gaiaId }
                        : {})), (result.cityId
                    ? { cityId: result.cityId }
                    : {})), (result.regionNames ? { regionNames: result.regionNames } : {})), (result.type ? { type: result.type } : {})), (result.subtype ? { subtype: result.subtype } : {})), (result.hotelId ? { selected: result.hotelId } : {})), (result.hierarchyInfo
                    ? { hierarchyInfo: result.hierarchyInfo }
                    : {})), (result.essId
                    ? {
                        essId: {
                            sourceId: result.essId.sourceId,
                            sourceName: result.essId.sourceName
                        }
                    }
                    : {})), { dest: _this.config.dest }), { index: index + 1 }), icon: getIcon(result.type, result.subtype, isChild, isNearby, result.category) }, (isChild ? { isChild: true } : {})), { name: primaryText, subtext: subtext, title: result.regionNames.lastSearchName ||
                    result.regionNames.shortName ||
                    result.regionNames.fullName, value: result.regionNames.lastSearchName ||
                    result.regionNames.shortName ||
                    result.regionNames.fullName });
            newResults.push(newResult);
            return newResults;
        };
        return adapt(results);
    };
    ESSAdapter.prototype.getBrowser = function (ua) {
        var tem;
        var M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            return 'IE';
        }
        if (M[1] === 'Chrome') {
            tem = ua.match(/\b(OPR|Edge?)\/(\d+)/);
            if (tem != null) {
                return tem
                    .slice(1, -1)
                    .join(' ')
                    .replace('OPR', 'Opera')
                    .replace('Edg ', 'Edge ');
            }
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, '-?'];
        tem = ua.match(/version\/(\d+)/i);
        if (tem != null) {
            M.splice(1, 1, tem[1]);
        }
        return M[0];
    };
    ESSAdapter.prototype.getDevice = function (ua) {
        if (/iPad|Transformer|ME173X|KOOF|Nook|IMM76D|Nexus 10|Nexus 7|Tablet/i.test(ua) ||
            /HP Slate|PG09410|PG41200|Mediapad|Kindle|Tab|Xoom|TegraNote|SCH-I800/i.test(ua)) {
            return 'Tablet';
        }
        else if (/Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
            return 'Mobile';
        }
        else {
            return 'Desktop';
        }
    };
    return ESSAdapter;
}());

var l10nStrings = {
    enteringInput: 'typeahead.enteringInput'
};
var TypeaheadRenderType;
(function (TypeaheadRenderType) {
    TypeaheadRenderType["MENU"] = "menu";
    TypeaheadRenderType["DIALOG"] = "dialog";
    TypeaheadRenderType["ADAPTIVE"] = "adaptive";
})(TypeaheadRenderType || (TypeaheadRenderType = {}));
var UitkTypeahead = forwardRef(function (props, ref) {
    var l10n = useLocalization();
    return createElement(Typeahead, __assign({ l10n: l10n, ref: ref }, props));
});
var Typeahead = /** @class */ (function (_super) {
    __extends(Typeahead, _super);
    function Typeahead(props) {
        var _this = _super.call(this, props) || this;
        _this.keyArray = [];
        _this.state = {
            dialog: false,
            mounted: false,
            searchTerm: '',
            startSearchTerm: ''
        };
        _this.typeaheadInputRef = createRef();
        _this.handleResultItemSelect = _this.handleResultItemSelect.bind(_this);
        _this.dismissTypeahead = _this.dismissTypeahead.bind(_this);
        _this.openTypeahead = _this.openTypeahead.bind(_this);
        _this.renderDialog = _this.renderDialog.bind(_this);
        _this.renderMenu = _this.renderMenu.bind(_this);
        _this.handleClear = _this.handleClear.bind(_this);
        _this.handleKeyPress = _this.handleKeyPress.bind(_this);
        _this.focusTypeaheadInput = _this.focusTypeaheadInput.bind(_this);
        _this.renderMenuTypeahead = _this.renderMenuTypeahead.bind(_this);
        _this.renderDialogTypeahead = _this.renderDialogTypeahead.bind(_this);
        _this.updateStartSearchTerm = _this.updateStartSearchTerm.bind(_this);
        return _this;
    }
    Typeahead.getDerivedStateFromProps = function (nextProps, prevState) {
        if (nextProps.value !== prevState.value) {
            return {
                searchTerm: nextProps.value
            };
        }
        return null;
    };
    Typeahead.prototype.focusTypeaheadInput = function () {
        liveAnnounce(this.props.l10n.formatText(l10nStrings.enteringInput));
        if (this.typeaheadInputRef.current) {
            this.typeaheadInputRef.current.focus();
        }
    };
    Typeahead.prototype.handleResultItemSelect = function (term, data) {
        if (this.props.onUpdate) {
            this.props.onUpdate({ term: term, data: data });
        }
        this.setState({
            searchTerm: term
        });
        this.focusTypeaheadInput();
    };
    Typeahead.prototype.handleKeyPress = function (e, triggerType) {
        var code = e.keyCode || e.which;
        var keyValue = e.key;
        if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
            this.keyArray.push(keyValue);
            var joinedKeyArray = this.keyArray.join('');
            if (triggerType === 'dialog') {
                this.updateStartSearchTerm(triggerType, joinedKeyArray);
            }
        }
    };
    Typeahead.prototype.dismissTypeahead = function (triggerType) {
        var onDismiss = this.props.onDismiss;
        if (typeof onDismiss === 'function') {
            onDismiss(triggerType);
        }
        this.setState({ dialog: false, startSearchTerm: '' });
        this.keyArray = [];
        if (this.typeaheadInputRef.current) {
            this.typeaheadInputRef.current.focus();
        }
    };
    Typeahead.prototype.updateStartSearchTerm = function (triggerType, keyValue) {
        var _this = this;
        this.setState({ startSearchTerm: keyValue }, function () {
            if (triggerType === 'dialog') {
                _this.openTypeahead(triggerType);
            }
        });
    };
    Typeahead.prototype.openTypeahead = function (triggerType) {
        var _this = this;
        var onClick = this.props.onClick;
        var previousState = this.state.dialog;
        // to handle menu open/close
        if (triggerType === TypeaheadRenderType.MENU) {
            if (previousState) {
                this.dismissTypeahead(triggerType);
            }
            else {
                if (typeof onClick === 'function') {
                    onClick(triggerType);
                }
                this.setState({ dialog: true });
                setTimeout(function () {
                    _this.props.adapter.typeaheadDialogOpened(_this.state.searchTerm);
                });
            }
        }
        // to handle dialog open/close
        else if (triggerType === TypeaheadRenderType.DIALOG) {
            if (typeof onClick === 'function') {
                onClick(triggerType);
            }
            this.setState({ dialog: true });
        }
    };
    Typeahead.prototype.renderDialog = function () {
        var _this = this;
        var _a = this.props, allowExactMatch = _a.allowExactMatch, id = _a.id, label = _a.label, listTitle = _a.listTitle, categorize = _a.categorize, onInputChange = _a.onInputChange, stid = _a.stid, toolbarVersion = _a.toolbarVersion, typeaheadPlaceholder = _a.typeaheadPlaceholder, typeaheadEmptyResultPlaceholder = _a.typeaheadEmptyResultPlaceholder, customTrigger = _a.customTrigger;
        var _b = this.state, dialog = _b.dialog, searchTerm = _b.searchTerm, startSearchTerm = _b.startSearchTerm;
        var adapter = this.getAdapter();
        if (categorize) {
            adapter.setTypeaheadCategories();
        }
        var additionalProps = {
            onDismiss: function () {
                _this.dismissTypeahead('dialog');
            }
        };
        return (createElement(UitkTypeaheadDialog, __assign({ adapter: adapter, allowExactMatch: allowExactMatch, dialog: (customTrigger && customTrigger.showTypeahead) || dialog, id: id, label: label }, additionalProps, { onInputChange: onInputChange, onResultItemSelect: this.handleResultItemSelect, term: searchTerm, startSearchTerm: startSearchTerm, stid: stid, toolbarVersion: toolbarVersion, placeholder: typeaheadPlaceholder, emptyResultPlaceholder: typeaheadEmptyResultPlaceholder, listTitle: listTitle, categorize: categorize, handleClear: this.handleClear })));
    };
    Typeahead.prototype.renderMenu = function () {
        var _this = this;
        var _a = this.props, allowExactMatch = _a.allowExactMatch, label = _a.label, listTitle = _a.listTitle, categorize = _a.categorize, onInputChange = _a.onInputChange, stid = _a.stid, toolbarVersion = _a.toolbarVersion, typeaheadPlaceholder = _a.typeaheadPlaceholder, typeaheadEmptyResultPlaceholder = _a.typeaheadEmptyResultPlaceholder, value = _a.value, customTrigger = _a.customTrigger, children = _a.children;
        var additionalProps = {
            onDismiss: function () {
                _this.dismissTypeahead('menu');
            },
            openTypeahead: function () {
                _this.openTypeahead('menu');
            }
        };
        var _b = this.state, dialog = _b.dialog, searchTerm = _b.searchTerm;
        var adapter = this.getAdapter();
        if (categorize) {
            adapter.setTypeaheadCategories();
        }
        return (createElement(UitkTypeaheadMenu, __assign({}, additionalProps, { value: value, label: label, adapter: adapter, dialog: (customTrigger && customTrigger.showTypeahead) || dialog, allowExactMatch: allowExactMatch, onInputChange: onInputChange, onResultItemSelect: this.handleResultItemSelect, toolbarVersion: toolbarVersion, stid: stid, term: searchTerm, placeholder: typeaheadPlaceholder, emptyResultPlaceholder: typeaheadEmptyResultPlaceholder, listTitle: listTitle, categorize: categorize, handleClear: this.handleClear }), customTrigger ? children : this.renderTrigger('menu')));
    };
    Typeahead.prototype.getAdapter = function () {
        if (this.props.adapter instanceof ESSAdapter) {
            this.props.adapter.setL10n(this.props.l10n);
        }
        return this.props.adapter;
    };
    Typeahead.prototype.renderTrigger = function (triggerType) {
        var _this = this;
        var _a = this.props, analytics = _a.analytics, icon = _a.icon, hasClearButton = _a.hasClearButton, handleClear = _a.handleClear, hasVisibleLabel = _a.hasVisibleLabel, invalid = _a.invalid, label = _a.label, name = _a.name, placeholder = _a.placeholder, stid = _a.stid, customTrigger = _a.customTrigger, children = _a.children;
        var searchTerm = this.state.searchTerm;
        var inputLabel = searchTerm === '' ? label : label + " " + searchTerm;
        var TriggerTag = triggerType === 'menu' ? UitkTextInput : UitkTypeaheadInput;
        if (customTrigger) {
            return children;
        }
        var additionalProps = {
            onClick: function () {
                if (triggerType === 'dialog') {
                    _this.openTypeahead(triggerType);
                }
            },
            onKeyPress: function (e) {
                _this.handleKeyPress(e, triggerType);
            }
        };
        return (createElement(TriggerTag, { className: this.state.mounted ? 'is-hidden' : '', "data-stid": stid && stid + "-input", domRef: this.typeaheadInputRef, hasClearButton: false, hasVisibleLabel: hasVisibleLabel, icon: icon, invalid: invalid, label: label, name: name, onChange: Typeahead.defaultProps.onInputChange, placeholder: placeholder, value: searchTerm, id: stid && stid + "-input" }, this.state.mounted && (createElement(UitkFauxInput, __assign({ "aria-label": inputLabel, label: label, hasClearButton: hasClearButton, handleClear: handleClear, "aria-describedby": invalid && stid ? stid + "-input-error" : undefined, "data-stid": stid && triggerType === 'menu'
                ? stid + "-menu-trigger"
                : stid && triggerType === 'dialog'
                    ? stid + "-dialog-trigger"
                    : '', domRef: this.typeaheadInputRef }, additionalProps, { onClickCapture: analytics && analyticsTracker.bind(this, analytics, '') }), searchTerm))));
    };
    Typeahead.prototype.renderMenuTypeahead = function (props) {
        var renderMenu = props.renderMenu;
        return createElement(Fragment, null, renderMenu ? renderMenu(this.renderMenu()) : this.renderMenu());
    };
    Typeahead.prototype.renderDialogTypeahead = function (props) {
        var renderDialog = props.renderDialog, customTrigger = props.customTrigger;
        var dialog = this.state.dialog;
        return (createElement(Fragment, null,
            renderDialog ? (dialog ? (renderDialog(this.renderDialog())) : null) : (createElement("div", { className: "uitk-dialog-layer " + ((customTrigger && customTrigger.showTypeahead) || this.state.dialog === true
                    ? 'uitk-dialog-layer-responsive'
                    : 'is-hidden') }, this.renderDialog())),
            this.renderTrigger('dialog')));
    };
    Typeahead.prototype.handleClear = function () {
        var handleClear = this.props.handleClear;
        if (typeof handleClear === 'function') {
            handleClear();
        }
    };
    Typeahead.prototype.componentDidMount = function () {
        var _a = this.props, value = _a.value, onMountCB = _a.onMountCB;
        if (value && value !== '') {
            this.setState({ searchTerm: value });
        }
        this.setState({ mounted: true });
        if (onMountCB) {
            onMountCB(this.typeaheadInputRef);
        }
    };
    Typeahead.prototype.render = function () {
        var _a = this.props, renderType = _a.renderType, stid = _a.stid;
        return (createElement("div", { className: "uitk-typeahead", "data-stid": stid }, renderType === TypeaheadRenderType.DIALOG ? (this.renderDialogTypeahead(this.props)) : renderType === TypeaheadRenderType.MENU ? (this.renderMenuTypeahead(this.props)) : renderType === TypeaheadRenderType.ADAPTIVE ? (createElement(Viewport, null,
            createElement(ViewSmall, null, this.renderDialogTypeahead(this.props)),
            createElement(ViewLarge, null, this.renderMenuTypeahead(this.props)))) : ('')));
    };
    Typeahead.displayName = 'UitkTypeahead';
    Typeahead.defaultProps = {
        hasClearButton: false,
        onInputChange: function () { return null; },
        renderType: TypeaheadRenderType.ADAPTIVE,
        toolbarVersion: TypeaheadToolbarVersion.V1
    };
    return Typeahead;
}(Component));

export { ESSAdapter, GoogleAdapter, PlacesServiceStatus, TypeaheadLocationType, TypeaheadRenderType, UitkTypeahead };
